
import { useMemo } from "react";
import { MetricCard } from "@/components/cards/MetricCard";
import { EngagementChart } from "@/components/charts/EngagementChart";
import { PieChartCard } from "@/components/charts/PieChartCard";
import { TopFBPosts, TopIGPosts } from "@/components/cards/TopPostCard";
import { fillDateRange } from "@/lib/chart-utils";
import { getPreviousPeriod } from "@/lib/period-compare";
import type { FBPost, IGPost, GA4Data } from "@/lib/types";
import { Eye, Heart, TrendingUp, Hash, BarChart3, Users, Target } from "lucide-react";
import { getPostTypeLabel } from "@/lib/post-type-labels";

interface WidgetProps {
  widgetId: string;
  fb: FBPost[];
  ig: IGPost[];
  ga4: GA4Data[];
  allFb: FBPost[];
  allIg: IGPost[];
  dateFrom: string;
  dateTo: string;
}

export function WidgetRenderer({ widgetId, fb, ig, ga4, allFb, allIg, dateFrom, dateTo }: WidgetProps) {
  const fbPosts = fb.filter((p) => p.post_id);
  const igPosts = ig.filter((p) => p.media_id);

  // Metrics
  const totalPosts = fbPosts.length + igPosts.length;
  const totalReach = fbPosts.reduce((s, p) => s + p.post_impressions_unique, 0) + igPosts.reduce((s, p) => s + p.media_reach, 0);
  const totalEng = fbPosts.reduce((s, p) => s + p.page_post_engagements, 0) + igPosts.reduce((s, p) => s + p.media_engagement, 0);
  const engRate = totalReach > 0 ? totalEng / totalReach : 0;
  const avgReach = totalPosts > 0 ? Math.round(totalReach / totalPosts) : 0;
  const avgEng = totalPosts > 0 ? Math.round(totalEng / totalPosts) : 0;
  const totalSessions = ga4.reduce((s, d) => s + d.sessions, 0);
  const totalConv = ga4.reduce((s, d) => s + d.conversions, 0);

  // Previous period
  const prev = useMemo(() => {
    const { dateFrom: pf, dateTo: pt } = getPreviousPeriod(dateFrom, dateTo);
    const pfb = allFb.filter((p) => p.post_id && p.date >= pf && p.date <= pt);
    const pig = allIg.filter((p) => p.media_id && p.date >= pf && p.date <= pt);
    const pp = pfb.length + pig.length;
    const pr = pfb.reduce((s, p) => s + p.post_impressions_unique, 0) + pig.reduce((s, p) => s + p.media_reach, 0);
    const pe = pfb.reduce((s, p) => s + p.page_post_engagements, 0) + pig.reduce((s, p) => s + p.media_engagement, 0);
    return { posts: pp, reach: pr, eng: pe, engRate: pr > 0 ? pe / pr : 0, avgReach: pp > 0 ? Math.round(pr / pp) : 0, avgEng: pp > 0 ? Math.round(pe / pp) : 0 };
  }, [allFb, allIg, dateFrom, dateTo]);

  // Chart data builders
  const engByDate = useMemo(() => {
    const map = new Map<string, { fb: number; ig: number }>();
    fbPosts.forEach((p) => { const c = map.get(p.date) || { fb: 0, ig: 0 }; c.fb += p.page_post_engagements; map.set(p.date, c); });
    igPosts.forEach((p) => { const c = map.get(p.date) || { fb: 0, ig: 0 }; c.ig += p.media_engagement; map.set(p.date, c); });
    return fillDateRange(Array.from(map.entries()).map(([date, { fb, ig }]) => ({ date, value: fb, value2: ig })), dateFrom, dateTo);
  }, [fbPosts, igPosts, dateFrom, dateTo]);

  const sessionsByDate = useMemo(() => {
    const map = new Map<string, number>();
    ga4.forEach((d) => map.set(d.date, (map.get(d.date) || 0) + d.sessions));
    return fillDateRange(Array.from(map.entries()).map(([date, value]) => ({ date, value })), dateFrom, dateTo);
  }, [ga4, dateFrom, dateTo]);

  const reachByDate = useMemo(() => {
    const map = new Map<string, number>();
    fbPosts.forEach((p) => map.set(p.date, (map.get(p.date) || 0) + p.post_impressions_unique));
    igPosts.forEach((p) => map.set(p.date, (map.get(p.date) || 0) + p.media_reach));
    return fillDateRange(Array.from(map.entries()).map(([date, value]) => ({ date, value })), dateFrom, dateTo);
  }, [fbPosts, igPosts, dateFrom, dateTo]);

  const trafficData = useMemo(() => {
    const map = new Map<string, number>();
    ga4.forEach((d) => map.set(d.default_channel_group, (map.get(d.default_channel_group) || 0) + d.sessions));
    return Array.from(map.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [ga4]);

  const deviceData = useMemo(() => {
    const map = new Map<string, number>();
    ga4.forEach((d) => map.set(d.devicecategory, (map.get(d.devicecategory) || 0) + d.sessions));
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [ga4]);

  const postTypeData = useMemo(() => {
    const map = new Map<string, number>();
    fbPosts.forEach((p) => map.set(p.type || "other", (map.get(p.type || "other") || 0) + 1));
    igPosts.forEach((p) => { const t = p.media_product_type || p.media_type || "other"; map.set(t, (map.get(t) || 0) + 1); });
    return Array.from(map.entries()).map(([name, value]) => ({ name: getPostTypeLabel(name), value }));
  }, [fbPosts, igPosts]);

  switch (widgetId) {
    case "metric-posts":
      return <MetricCard label="Příspěvků" value={totalPosts} previousValue={prev.posts} icon={<Hash className="h-4 w-4 text-primary" />} subtitle={`FB: ${fbPosts.length} | IG: ${igPosts.length}`} />;
    case "metric-reach":
      return <MetricCard label="Dosah" value={totalReach} previousValue={prev.reach} icon={<Eye className="h-4 w-4 text-primary" />} />;
    case "metric-engagement":
      return <MetricCard label="Engagement" value={totalEng} previousValue={prev.eng} icon={<Heart className="h-4 w-4 text-primary" />} />;
    case "metric-engrate":
      return <MetricCard label="Eng. rate" value={engRate} previousValue={prev.engRate} format="percent" icon={<TrendingUp className="h-4 w-4 text-primary" />} />;
    case "metric-avg-reach":
      return <MetricCard label="Ø dosah" value={avgReach} previousValue={prev.avgReach} icon={<Eye className="h-4 w-4 text-primary" />} subtitle="na post" />;
    case "metric-avg-eng":
      return <MetricCard label="Ø eng." value={avgEng} previousValue={prev.avgEng} icon={<BarChart3 className="h-4 w-4 text-primary" />} subtitle="na post" />;
    case "metric-sessions":
      return <MetricCard label="Sessions" value={totalSessions} icon={<Users className="h-4 w-4 text-primary" />} />;
    case "metric-conversions":
      return <MetricCard label="Konverze" value={totalConv} icon={<Target className="h-4 w-4 text-primary" />} />;
    case "chart-engagement":
      return <EngagementChart title="Engagement - FB vs IG" data={engByDate} dataKey="value" dataKey2="value2" color="#c9a84c" color2="#d4816b" label1="Facebook" label2="Instagram" fbPosts={fbPosts} igPosts={igPosts} />;
    case "chart-sessions":
      return <EngagementChart title="Sessions (GA4)" data={sessionsByDate} color="#7c9a5e" label1="Sessions" />;
    case "chart-reach":
      return <EngagementChart title="Dosah trend" data={reachByDate} color="#6b8fd4" label1="Dosah" fbPosts={fbPosts} igPosts={igPosts} />;
    case "chart-traffic":
      return <PieChartCard title="Zdroje návštěvnosti" data={trafficData} />;
    case "chart-devices":
      return <PieChartCard title="Zařízení" data={deviceData} />;
    case "chart-post-types":
      return <PieChartCard title="Typy příspěvků" data={postTypeData} />;
    case "top-fb":
      return <TopFBPosts posts={fbPosts} limit={5} />;
    case "top-ig":
      return <TopIGPosts posts={igPosts} limit={5} />;
    default:
      return <div className="p-4 text-muted-foreground">Neznámý widget: {widgetId}</div>;
  }
}
